import { Dispatch, SetStateAction } from "react";

export type TabsType = "diagrams" | "shapes" | "composition" | "chat";

export interface DiagramInfo {
  _id?: string;
  name?: string;
  version?: string;
  userId?: string;
  uuid: string;
  metadata?: {
    description: string;
    svgContent: string;
    project: string;
    priority: string;
  } | null;
  diagramComponents: DiagramComponent[];
}

export interface DiagramComponent {
  id: string;
  shape: string;
  source?: "shape" | "component"; // Indicates if this is from SVG library or component library
  type?: string; // References componentType from schema (optional)
  position:
    | "center"
    | "top"
    | "front-right"
    | "front-left"
    | "back-right"
    | "back-left"
    | string;
  relativeToId: string | null;
  attached2DShapes: Attached2DShape[];
  attachmentPoints?: AttachmentPoint[];
  parentAttachmentPoints?: AttachmentPoint[];
  absolutePosition?: Point;
  cut?: boolean;
  metadata?: {
    // Optional metadata based on component type schema
    [key: string]: any;
  };
}
export interface Attached2DShape {
  name: string;
  attachedTo: string;
}

export interface Point {
  x: number;
  y: number;
}
export interface AttachmentPoint extends Point {
  name: string;
}

export type PanelProps = {
  mode: string;
  uuid?: string;
  messageId?: number | null;
  message?: string;
  // BREEZEAI-785: standalone HTML render of the functional-document (FRD),
  // sourced from document.metadata.htmlContent[0].html. When present, the
  // markdown viewer toolbar shows an "Open" button that opens it in a new tab.
  htmlContent?: string;
  website?: string | null;
  screen?: string | null;
  metadata?: Record<string, any>;
  tab?: "Functional" | "Design" | "Architecture" | "History";
  // set with `tab` to switch an already-open unified-model panel to that tab (a new value each request)
  tabRequestedAt?: number;
} | null;
export interface PanelPropsContextType {
  panelProps: PanelProps;
  setPanelProps: Dispatch<SetStateAction<PanelProps>>;
}

export interface CanvasSize {
  width: number;
  height: number;
}
export interface Attached2DShape {
  name: string;
  attachedTo: string;
}

export interface Shape {
  name: string;
  type: "2D" | "3D" | "LAYERS"; // Changed to specific string literal types
  attachTo?: string;
  svgFile: string;
  svgContent: string;
  description?: string;
  path?: string;
  version?: string;
  tags?: string[];
  status?: "active" | "inactive";
  _id?: string;
}

export interface Component {
  _id?: string;
  id: string;
  name: string;
  description: string;
  diagramComponents: DiagramComponent[];
  attachmentPoints: AttachmentPoint[];
  svgContent?: string;
  path?: string;
  version?: string;
  created: Date;
  lastModified: Date;
  tags?: string[];
  status?: "active" | "inactive";
}

export interface Category {
  _id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  name: string;
  description: string | null;
  parent: string | null;
  path: string;
  metadata: any | null;
  ancestors: string[];
  children: Category[];
  level?: number;
  shapeCount: number;
}
export interface UnifiedElement {
  _id?: string;
  id: string;
  name: string;
  description: string;
  type: "2D" | "3D" | "LAYERS" | "COMPONENT"; // Changed to specific string literal types
  attachTo?: string;
  diagramComponents?: DiagramComponent[];
  attachmentPoints?: AttachmentPoint[];
  svgFile?: string;
  svgContent?: string;
  path?: string;
  version?: string;
  created?: Date;
  lastModified?: Date;
  tags?: string[];
  status: "active" | "inactive";
}

export type ElementType = "3D" | "2D" | "LAYERS" | "COMPONENT";

export interface ShapesPanelProps {
  svgLibrary: Shape[];
  shapesByCategory: Shape[];
  searchQuery: string;
  searchedData:
    | {
        data: UnifiedElement[];
        total: number;
      }
    | undefined;
  setSearchQuery: (newLibrary: string) => void;
  canvasSize: CanvasSize;
  categories: Category[];
  activeCategory: string;
  onCategoryChange: (id: string) => void;
  onAdd3DShape: (shapeName: string) => void;
  onAdd2DShape: (shapeName: string, attachTo: string) => void;
  onAddComponent: (component: Component) => void;
  onDeleteComponent: (componentId: string) => void;
  selected3DShape: string | null;
  diagramComponents: DiagramComponent[];
  components: Component[];
  activeLibrary: string;
  isDataLoading: {
    isCategoryLoading: boolean;
    isSearchLoading: boolean;
    isShapesLoading: boolean;
  };
}

// Types for Points Library
export enum Direction {
  N = "N",
  S = "S",
  W = "W",
  E = "E",
}
export interface AttachmentPointMap {
  [key: string]: AttachmentPoint;
}

// Map of array of all attachment points for a component from its constituent shapes
export interface ComponentAttachmentPointMap {
  [key: string]: AttachmentPoint[];
}

export interface SerializedDiagramComponent {
  id: string;
  shape: string;
  source?: "shape" | "component";
  position:
    | "center"
    | "top"
    | "front-right"
    | "front-left"
    | "back-right"
    | "back-left"
    | string;
  relativeToId: string | null;
  attachmentPoints?: AttachmentPoint[];

  attached2DShapes: {
    name: string;
    attachedTo: string;
  }[];
  type?: string; // Add type field
  metadata?: Record<string, any>; // Add metadata field
}

export interface GlobalAttachmentPoint {
  componentId: string; // ID of the component this point belongs to
  attachmentPoints: AttachmentPoint[];
}

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TransformationContext {
  viewBox: ViewBox;
  canvasSize: CanvasSize;
  margin: number;
  scale: number;
}

export interface LibraryData {
  id: string;
  name: string;
  description: string;
  shapes: Shape[];
  spreadsheetUrl?: string;
  folderUrl?: string;
  lastUpdated: Date;
  isLoading?: boolean;
}
export interface ShapesLibrary {
  shapes: { [key: string]: Shape };
  lastModified: Date;
}

export interface ComponentLibrary {
  components: { [key: string]: Component };
  lastModified: Date;
}
type Metadata = {
  description: string;
  applicationTypes: string[];
  customProperties: { [key: string]: string };
  dependencies: { shapeId: string; version: string }[];
};
export type UnifiedResponse = {
  _id: string;
  createdAt: string;
  updatedAt: string;
  status: "active" | "inactive";
  name: string;
  type: "3D" | "2D" | "COMPONENT" | "LAYERS";
  attachTo: string | null;
  svgFile: string | null;
  svgContent: string | null;
  version: string;
  category: string;
  metadata: Metadata | null;
  tags: string[] | null;
  author: string | null;
  diagram_components: DiagramComponent[];
  attachment_points: AttachmentPoint[];
  categoryDetails?: {
    _id: string;
    path: string;
  };
};
export interface LayerLabelSettings {
  width: number;
  lineSpacing: number;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  hideLabels: boolean;
}
export interface CanvasSizeSettings {
  canvasSize: CanvasSize;
  showAttachmentPoints: boolean;
}
export interface MetadataLabelSettings {
  minSpacing: number; // minimum X spacing between metadata labels
  minYSpacing: number; // minimum Y spacing between metadata labels
  smoothingAngle: number; // hull smoothing angle in radians
  stepSize: number; // distance between metadata label positions
}

export interface CanvasSettings {
  canvas: CanvasSizeSettings;
  metadataLabel: MetadataLabelSettings;
  layerLabel: LayerLabelSettings;
}

export interface MetadataFieldOption {
  label: string;
  value: string;
}

export interface MetadataField {
  name: string;
  type: "string" | "select";
  label: string;
  required?: boolean;
  options?: MetadataFieldOption[];
}

export interface ComponentType {
  displayName: string;
  fields: MetadataField[];
}

export interface ComponentTypes {
  [key: string]: ComponentType;
}
export interface SchemaDefinition {
  componentTypes: ComponentTypes;
}
